import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, IconButton } from "@mui/material";
import Table from "../../../components/table/table";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderIssuesRequest,
  getOrderIssuesSuccess,
} from "../../../redux/orders/slice";
import dayjs from "dayjs";
import TableHeaderSelectField from "../../../components/table-header-select-field/table-header-select-field";
import TableHeaderDatePicker from "../../../components/table-header-datepicker/table-header-datepicker";
import { userType } from "../../../utils/Constant";
import AcceptanceIssueDetails from "./acceptance-issue-details/acceptance-issue-details";
import IssueDetails from "./issue-details/issue-details";
import UpdateIssuesDialog from "./update-issues-dialog/update-issues-dialog";
import Notification from "../../../components/notification/notification";

export default function TenantIssues() {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({});
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [selectedRowData, setSelectedRowData] = useState<any>();
  const [showIssueDetails, setShowIssueDetails] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

    const [filterResetKey, setFilterResetKey] = useState(0);

  const { loading, orderIssues } = useSelector((state: any) => state.orders);
  const { currentUser } = useSelector((state: any) => state.user.user);

  const rowCountRef = useRef(orderIssues?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (orderIssues?.totalCount !== undefined) {
      rowCountRef.current = orderIssues?.totalCount;
    }
    return rowCountRef.current;
  }, [orderIssues?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "orderNumber",
      headerName: "Order ID",
      flex: 1,
      sortable: false,
      valueGetter: (value, row) => {
        const orderNumbers = row?.orders
          ?.map((order: any) => order?.orderNumber)
          .join(", ");
        return orderNumbers;
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="orderNumber"
          sortField="orderid"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          isSortable={false}
        />
      ),
    },
    {
      field: "createdOrUpdatedAt",
      headerName: "Last Updated",
      flex: 1,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY, h:mma") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="createdOrUpdatedAt"
          sortField="last_updated"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "client",
      headerName: "Client",
      flex: 1,
      sortable: false,
      valueGetter: (value: any) => {
        return `${value?.clientName || "-"}`;
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="ClientName"
          sortField="clientname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "inspectorFirm",
      headerName: "Inspector",
      flex: 1,
      sortable: false,
      valueGetter: (value: any) => {
        return `${value?.displayName || "-"}`;
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="InspectorFirmName"
          sortField="inspectorfirmname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "description",
      headerName: "Issue",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="description"
          sortField="description"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "isResolved",
      headerName: "Status",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Resolved" : "Unresolved"}
          color={params.value ? "success" : "error"}
        />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isResolved"
          sortField="is_resolved"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Resolved", "Unresolved"]}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    onFilterUsers({ sortColumn: "is_resolved", sortOrder: "asc" });
    dispatch(getOrderIssuesSuccess({ response: null }));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (orderIssues) {
      setRowData(orderIssues?.items);
    }
  }, [orderIssues]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getOrderIssuesRequest({ type: "Tenant", ...filters }));
    }
  }, [filterParams]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const checkResolvedIssues = () => {
    const unResolvedIssues = selectedRowData.filter(
      (data: any) => data?.isResolved === false
    );
    return unResolvedIssues.length === selectedRowData.length ? false : true;
  };

  const onUpdateStatusSuccess = () => {
    setSuccessMessage("Issues Updated Successfully.");
    setShowSuccessNotification(true);
    dispatch(
      getOrderIssuesRequest({
        type: "Tenant",
        isResolved: false,
        ...filterParams,
      })
    );
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      page: 1,
      pageSize: 10,
      sortColumn: "is_resolved",
      sortOrder: "asc",
    } as any);
    setFilterResetKey((prev) => prev + 1);
  };

  return (
    <>
      <Box
        className="clients-header"
        sx={{
          mt: 3,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          {rowSelectionModel.length > 1 ? (
            <Button
              sx={{ fontWeight: "550" }}
              variant="text"
              disabled={checkResolvedIssues()}
              onClick={() => setShowUpdateDialog(true)}
            >
              Update Selected Issues
            </Button>
          ) : (
            <Button
              sx={{ fontWeight: "550" }}
              variant="text"
              disabled={rowSelectionModel.length !== 1}
              onClick={() => setShowIssueDetails(true)}
            >
              View/Update Issue
            </Button>
          )}
        </Box>
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={"issues-tenant-issues-table-" + filterResetKey}
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection={true}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
            const selected = newRowSelectionModel.map((id: string) =>
              rowData.find((r: any) => r.id === id)
            );
            setSelectedRowData(selected);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      </Box>

      {showIssueDetails && [
        currentUser?.userType === userType.tenant ? (
          <IssueDetails
            issueId={selectedRowData[0]?.id}
            showDialog={showIssueDetails}
            closeDialog={() => setShowIssueDetails(false)}
          />
        ) : (
          <AcceptanceIssueDetails
            issueId={selectedRowData[0]?.id}
            showDialog={showIssueDetails}
            closeDialog={() => setShowIssueDetails(false)}
          />
        ),
      ]}

      {showUpdateDialog && (
        <UpdateIssuesDialog
          issueIds={rowSelectionModel}
          showDialog={showUpdateDialog}
          closeDialog={() => setShowUpdateDialog(false)}
          updateSuccess={onUpdateStatusSuccess}
        />
      )}

      {showSuccessNotification && (
        <Notification
          show={showSuccessNotification}
          message={successMessage}
          onClose={() => setShowSuccessNotification(false)}
        />
      )}
    </>
  );
}
